import React, { useRef, useState } from 'react';
import 'cropperjs/dist/cropper.css';
import { Cropper } from 'react-cropper';
import { Modal, Button, ButtonGroup } from 'react-bootstrap';
import { createImageEditURL } from '../../utils/umoUtils';
import { injectIntl } from 'react-intl';
import { downloadExportFile } from '../../utils/uiUtils';
import FlipHorizontal from '../Icons/FlipHorizontal';
import FlipVertical from '../Icons/FlipVertical';
import PenOff from '../Icons/PenOff';
import {
  UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR,
  UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE,
} from '../../constants/umoModifications';
import Loader from '../ui/Loader';
import { rotateImage } from '../../utils/piction';

const ImageEditModal = ({ modalData, onModalCloseClick, dispatch, intl }) => {
  const ANGLE_STEP = 45;

  const umoUrl = modalData.get('umoUrl');
  const umoID = modalData.get('umoId');
  const modalType = modalData.get('modType');
  const action = modalType === UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE ? 'ROTATE' : 'ADJUST';

  const cropperRef = useRef(null);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [isMirroredX, setIsMirroredX] = useState(false);
  const [isMirroredY, setIsMirroredY] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const rotateLeft = () => {
    setRotationAngle((prev) => {
      const newAngle = (prev - ANGLE_STEP + 360) % 360;
      return newAngle;
    });
    const cropper = cropperRef.current.cropper;
    cropper.rotate(-ANGLE_STEP);
  };

  const rotateRight = () => {
    setRotationAngle((prev) => (prev + ANGLE_STEP) % 360);
    const cropper = cropperRef.current.cropper;
    cropper.rotate(ANGLE_STEP);
  };

  const mirrorX = () => {
    const cropper = cropperRef.current.cropper;
    cropper.scaleX(cropper.getData().scaleX * -1);
    setIsMirroredX((prev) => !prev);
  };

  const mirrorY = () => {
    const cropper = cropperRef.current.cropper;
    cropper.scaleY(cropper.getData().scaleY * -1);
    setIsMirroredY((prev) => !prev);
  };

  const resetTransformations = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) cropper.reset().scaleX(0.8).scaleY(0.8);
    setRotationAngle(0);
    setIsMirroredX(false);
    setIsMirroredY(false);
    setIsLoading(false);
  };

  const handleCloseModal = () => {
    onModalCloseClick();
    resetTransformations();
  };

  const handleDownload = () => {
    setIsLoading(true);
    const options = [
      { DOWNLOAD: 'TRUE' },
      { DOWNLOAD_IMAGE: 'ORIGINAL' },
      { DOWNLOAD_IMAGE_ID: '1' },
      { DOWNLOAD_AS_ATTACHMENT: 'ATTACH' } 
    ];
    if (modalType === UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE) {
      options.push(
        { action: 'ROTATE' },
        { ROTATE: rotationAngle === 0 ? 360 : rotationAngle }
      );
    } else if (modalType === UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR) {
      options.push(
        { action: 'ADJUST' },
        { EXTRA_IMAGEMAGICK: `${isMirroredX ? '-flop' : ''} ${isMirroredY ? '-flip' : ''}`.trim() }
      );
    }

    downloadExportFile(createImageEditURL(umoID, options), dispatch);
    handleCloseModal();
  };

  const handleCreate = () => {
    setIsLoading(true);
    const options = {
      UMO_ID: umoID,
      DOWNLOAD: 'FALSE',
      CREATE_NEW_UMO: 'TRUE',
    };
    if (modalType === UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE) {
      options.action = 'ROTATE';
      options.ROTATE = rotationAngle === 0 ? 360 : rotationAngle;
    } else if (modalType === UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR) {
      options.action = 'ADJUST';
      options.EXTRA_IMAGEMAGICK = `${isMirroredX ? '-flop' : ''} ${isMirroredY ? '-flip' : ''}`.trim();
    }
    rotateImage(umoID, options, dispatch)
    .then((res) => {
      if(res.result === 'success') {
        handleCloseModal();
      }
    })
    .catch((err) => {
      setIsLoading(false);
    });
  };

  return (
    <div className="image-edit-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({
            id: modalType == UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE ?
            'imageEdit.rotateImage' : 'imageEdit.mirrorImage'
            })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="edit-container">
          <Cropper
            className="cropper"
            initialAspectRatio={1}
            src={umoUrl}
            ref={cropperRef}
            viewMode={1}
            guides={false}
            autoCrop={false}
            dragMode="move"
            background={false}
            scaleX={0.8}
            scaleY={0.8}
            zoomable={true}
            responsive={true}
            rotatable={true}
            rotateTo={rotationAngle}
            checkOrientation={false}
          />
        </div>
        {modalType == UMO_MODIFY_TYPE_IMAGE_EDIT_ROTATE && (
          <div className="button-group-container">
            <ButtonGroup size="lg" className="mb-2">
              <Button onClick={rotateLeft}>
                <i className="rotate-left-icon" />
              </Button>
              <Button onClick={rotateRight}>
                <i className="rotate-right-icon" />
              </Button>
              <Button onClick={resetTransformations}>
                <PenOff stroke={'white'} width={22} height={22} />
              </Button>
            </ButtonGroup>
          </div>
        )}
        {modalType == UMO_MODIFY_TYPE_IMAGE_EDIT_MIRRROR && (
          <div className="button-group-container">
            <ButtonGroup size="lg" className="mb-2">
              <Button onClick={mirrorX}>
                <FlipHorizontal stroke={'white'} width={22} height={22} />
              </Button>
              <Button onClick={mirrorY}>
                <FlipVertical stroke={'white'} width={22} height={22} />
              </Button>
              <Button onClick={resetTransformations}>
                <PenOff stroke={'white'} width={22} height={22} />
              </Button>
            </ButtonGroup>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline" onClick={handleCloseModal} disabled={isLoading}>
          {intl.formatMessage({ id: 'general.close' })}
        </Button>
        <Button variant="primary" onClick={handleDownload} disabled={isLoading}>
          {intl.formatMessage({ id: 'general.download' })}
        </Button>
      </Modal.Footer>
    </div>
  );
};

export default injectIntl(ImageEditModal);
